'use client';

/**
 * Endpoint Editor Component
 * Edit endpoint details, parameters, request body, and responses
 */

import { useState, useCallback } from 'react';
import {
  Endpoint,
  Parameter,
  Response,
  HttpMethod,
  ParameterLocation,
  SchemaType,
  SchemaDefinition,
  HTTP_METHOD_CONFIG,
  COMMON_STATUS_CODES,
  DEFAULT_RESPONSES,
} from '@/lib/api-contract/types';

// Property interface for schema builder
interface SchemaProperty {
  id: string;
  name: string;
  type: SchemaType;
  description?: string;
  required?: boolean;
  example?: string;
}

// JSON Schema Builder Component
function JsonSchemaBuilder({
  schema,
  onChange,
  example,
  onExampleChange,
  canEdit = true,
}: {
  schema: SchemaDefinition;
  onChange: (schema: SchemaDefinition) => void;
  example?: unknown;
  onExampleChange?: (example: unknown | undefined) => void;
  canEdit?: boolean;
}) {
  const [showExample, setShowExample] = useState(false);
  const [exampleError, setExampleError] = useState<string | null>(null);

  // Convert schema properties to editable list
  // Note: schema.required can be boolean or string[] in OpenAPI - we handle both
  const requiredFields = Array.isArray(schema.required) ? schema.required : [];
  const properties: SchemaProperty[] = Object.entries(schema.properties || {}).map(
    ([name, prop]) => ({
      id: name,
      name,
      type: (prop as SchemaDefinition).type || 'string',
      description: (prop as SchemaDefinition).description,
      required: requiredFields.includes(name),
      example: (prop as SchemaDefinition).example !== undefined
        ? String((prop as SchemaDefinition).example)
        : undefined,
    })
  );

  const addProperty = () => {
    const newName = `property${properties.length + 1}`;
    onChange({
      ...schema,
      properties: {
        ...schema.properties,
        [newName]: { type: 'string' },
      },
    });
  };

  const updateProperty = (oldName: string, updates: Partial<SchemaProperty>) => {
    const newProperties = { ...schema.properties };
    const currentProp = newProperties[oldName] as SchemaDefinition;

    // Handle name change
    if (updates.name && updates.name !== oldName) {
      delete newProperties[oldName];
      newProperties[updates.name] = {
        ...currentProp,
        type: updates.type || currentProp.type,
        description: updates.description !== undefined ? updates.description : currentProp.description,
        example: updates.example !== undefined ? updates.example : currentProp.example,
      };

      // Update required array
      const currentRequired = Array.isArray(schema.required) ? schema.required : [];
      let newRequired = currentRequired.filter((r) => r !== oldName);
      if (updates.required) {
        newRequired = [...newRequired, updates.name];
      }

      onChange({
        ...schema,
        properties: newProperties,
        required: newRequired.length > 0 ? newRequired : undefined,
      });
    } else {
      // Just update the property
      newProperties[oldName] = {
        ...currentProp,
        type: updates.type || currentProp.type,
        description: updates.description !== undefined ? updates.description : currentProp.description,
        example: updates.example !== undefined ? updates.example : currentProp.example,
      };

      // Handle required change
      let newRequired = Array.isArray(schema.required) ? [...schema.required] : [];
      if (updates.required !== undefined) {
        if (updates.required && !newRequired.includes(oldName)) {
          newRequired = [...newRequired, oldName];
        } else if (!updates.required) {
          newRequired = newRequired.filter((r) => r !== oldName);
        }
      }

      onChange({
        ...schema,
        properties: newProperties,
        required: newRequired.length > 0 ? newRequired : undefined,
      });
    }
  };

  const deleteProperty = (name: string) => {
    const newProperties = { ...schema.properties };
    delete newProperties[name];
    onChange({
      ...schema,
      properties: newProperties,
      required: Array.isArray(schema.required) ? schema.required.filter((r) => r !== name) : undefined,
    });
  };

  // Generate example JSON from schema
  const generateExample = useCallback(() => {
    const exampleObj: Record<string, unknown> = {};
    Object.entries(schema.properties || {}).forEach(([name, prop]) => {
      const propDef = prop as SchemaDefinition;
      if (propDef.example !== undefined) {
        exampleObj[name] = propDef.example;
      } else {
        switch (propDef.type) {
          case 'string':
            exampleObj[name] = 'string';
            break;
          case 'number':
            exampleObj[name] = 0;
            break;
          case 'integer':
            exampleObj[name] = 0;
            break;
          case 'boolean':
            exampleObj[name] = false;
            break;
          case 'array':
            exampleObj[name] = [];
            break;
          case 'object':
            exampleObj[name] = {};
            break;
          default:
            exampleObj[name] = null;
        }
      }
    });
    return exampleObj;
  }, [schema.properties]);

  const handleExampleChange = (value: string) => {
    if (!onExampleChange) return;
    if (!value.trim()) {
      onExampleChange(undefined);
      setExampleError(null);
      return;
    }
    try {
      const parsed = JSON.parse(value);
      onExampleChange(parsed);
      setExampleError(null);
    } catch {
      setExampleError('Invalid JSON');
    }
  };

  return (
    <div className="space-y-3">
      {/* Properties Header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">Properties</span>
        {canEdit && (
          <button
            onClick={addProperty}
            className="text-xs px-2 py-1 bg-accent-muted text-accent rounded hover:bg-accent/30"
          >
            + Add Property
          </button>
        )}
      </div>

      {/* Properties List */}
      {properties.length === 0 ? (
        <p className="text-foreground-tertiary text-sm text-center py-4">No properties defined</p>
      ) : (
        <div className="space-y-2">
          {properties.map((prop) => (
            <div
              key={prop.id}
              className="bg-cream/5 border border-border-muted rounded-lg p-3"
            >
              <div className="grid grid-cols-12 gap-2 items-start">
                {/* Name */}
                <div className="col-span-4">
                  <input
                    type="text"
                    value={prop.name}
                    onChange={(e) => updateProperty(prop.name, { name: e.target.value })}
                    placeholder="name"
                    disabled={!canEdit}
                    className="w-full px-2 py-1 bg-cream/10 border border-border rounded text-foreground text-sm font-mono focus:outline-none focus:border-accent disabled:opacity-50"
                  />
                </div>

                {/* Type */}
                <div className="col-span-3">
                  <select
                    value={prop.type}
                    onChange={(e) => updateProperty(prop.name, { type: e.target.value as SchemaType })}
                    disabled={!canEdit}
                    className="w-full px-2 py-1 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
                  >
                    <option value="string">string</option>
                    <option value="number">number</option>
                    <option value="integer">integer</option>
                    <option value="boolean">boolean</option>
                    <option value="array">array</option>
                    <option value="object">object</option>
                  </select>
                </div>

                {/* Required checkbox */}
                <div className="col-span-3 flex items-center">
                  <label className="flex items-center gap-1.5 text-xs text-foreground-secondary">
                    <input
                      type="checkbox"
                      checked={prop.required || false}
                      onChange={(e) => updateProperty(prop.name, { required: e.target.checked })}
                      disabled={!canEdit}
                      className="rounded"
                    />
                    required
                  </label>
                </div>

                {/* Delete */}
                <div className="col-span-2 flex justify-end">
                  {canEdit && (
                    <button
                      onClick={() => deleteProperty(prop.name)}
                      className="p-1 text-foreground-muted hover:text-red-400"
                      title="Delete property"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Description row */}
              <div className="mt-2">
                <input
                  type="text"
                  value={prop.description || ''}
                  onChange={(e) => updateProperty(prop.name, { description: e.target.value || undefined })}
                  placeholder="Description (optional)"
                  disabled={!canEdit}
                  className="w-full px-2 py-1 bg-cream/5 border border-border-muted rounded text-foreground-secondary text-xs focus:outline-none focus:border-accent disabled:opacity-50"
                />
              </div>

              {/* Example row */}
              <div className="mt-2">
                <input
                  type="text"
                  value={prop.example || ''}
                  onChange={(e) => updateProperty(prop.name, { example: e.target.value || undefined })}
                  placeholder="Example value (optional)"
                  disabled={!canEdit}
                  className="w-full px-2 py-1 bg-cream/5 border border-border-muted rounded text-foreground-secondary text-xs font-mono focus:outline-none focus:border-accent disabled:opacity-50"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Example JSON Section */}
      {onExampleChange && (
        <div className="border-t border-border-muted pt-3 mt-3">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => setShowExample(!showExample)}
              className="text-sm text-foreground-secondary hover:text-foreground flex items-center gap-1"
            >
              <svg
                className={`w-4 h-4 transition-transform ${showExample ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Example JSON
            </button>
            {canEdit && properties.length > 0 && (
              <button
                onClick={() => onExampleChange(generateExample())}
                className="text-xs px-2 py-1 text-accent hover:underline"
              >
                Generate from schema
              </button>
            )}
          </div>

          {showExample && (
            <div>
              <textarea
                value={example ? JSON.stringify(example, null, 2) : ''}
                onChange={(e) => handleExampleChange(e.target.value)}
                placeholder='{\n  "property": "value"\n}'
                disabled={!canEdit}
                rows={6}
                className={`w-full px-3 py-2 bg-cream/10 border rounded text-foreground font-mono text-sm focus:outline-none disabled:opacity-50 resize-none ${
                  exampleError ? 'border-red-500' : 'border-border focus:border-accent'
                }`}
              />
              {exampleError && (
                <p className="text-xs text-red-400 mt-1">{exampleError}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface EndpointEditorProps {
  endpoint: Endpoint;
  tags: string[];
  onChange: (endpoint: Endpoint) => void;
  canEdit?: boolean;
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function EndpointEditor({
  endpoint,
  tags,
  onChange,
  canEdit = true,
}: EndpointEditorProps) {
  const [activeTab, setActiveTab] = useState<'general' | 'parameters' | 'headers' | 'requestBody' | 'responses'>('general');

  // Count headers (parameters with in='header')
  const headerCount = endpoint.parameters.filter(p => p.in === 'header').length;

  const updateEndpoint = (updates: Partial<Endpoint>) => {
    onChange({ ...endpoint, ...updates });
  };

  const addParameter = () => {
    const newParam: Parameter = {
      id: generateId(),
      name: '',
      in: 'query',
      required: false,
      schema: { type: 'string' },
    };
    updateEndpoint({ parameters: [...endpoint.parameters, newParam] });
  };

  const updateParameter = (id: string, updates: Partial<Parameter>) => {
    updateEndpoint({
      parameters: endpoint.parameters.map((p) =>
        p.id === id ? { ...p, ...updates } : p
      ),
    });
  };

  const deleteParameter = (id: string) => {
    updateEndpoint({
      parameters: endpoint.parameters.filter((p) => p.id !== id),
    });
  };

  const addResponse = (statusCode: string) => {
    const defaultResponse = DEFAULT_RESPONSES[statusCode];
    const newResponse: Response = {
      id: generateId(),
      statusCode,
      description: defaultResponse?.description || COMMON_STATUS_CODES[statusCode] || 'Response',
      content: defaultResponse?.schema ? {
        'application/json': { schema: defaultResponse.schema },
      } : undefined,
    };
    updateEndpoint({ responses: [...endpoint.responses, newResponse] });
  };

  const updateResponse = (id: string, updates: Partial<Response>) => {
    updateEndpoint({
      responses: endpoint.responses.map((r) =>
        r.id === id ? { ...r, ...updates } : r
      ),
    });
  };

  const deleteResponse = (id: string) => {
    updateEndpoint({
      responses: endpoint.responses.filter((r) => r.id !== id),
    });
  };

  // Count non-header parameters
  const paramCount = endpoint.parameters.filter(p => p.in !== 'header').length;

  const tabs = [
    { id: 'general', label: 'General' },
    { id: 'parameters', label: `Parameters (${paramCount})` },
    { id: 'headers', label: `Headers (${headerCount})` },
    { id: 'requestBody', label: 'Request Body' },
    { id: 'responses', label: `Responses (${endpoint.responses.length})` },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3 mb-3">
          <span
            className="px-3 py-1 text-sm font-bold rounded"
            style={{
              backgroundColor: HTTP_METHOD_CONFIG[endpoint.method].bgColor,
              color: HTTP_METHOD_CONFIG[endpoint.method].color,
            }}
          >
            {endpoint.method}
          </span>
          <code className="text-foreground font-mono">{endpoint.path || '/path'}</code>
          {endpoint.deprecated && (
            <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded">
              Deprecated
            </span>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`px-3 py-1.5 text-sm rounded transition-colors ${
                activeTab === tab.id
                  ? 'bg-accent text-accent-foreground font-medium'
                  : 'text-foreground-secondary hover:bg-cream/10'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* General Tab */}
        {activeTab === 'general' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-foreground-secondary mb-1">Method</label>
                <select
                  value={endpoint.method}
                  onChange={(e) => updateEndpoint({ method: e.target.value as HttpMethod })}
                  disabled={!canEdit}
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent disabled:opacity-50"
                >
                  {(Object.keys(HTTP_METHOD_CONFIG) as HttpMethod[]).map((method) => (
                    <option key={method} value={method}>
                      {method}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-foreground-secondary mb-1">Path *</label>
                <input
                  type="text"
                  value={endpoint.path}
                  onChange={(e) => updateEndpoint({ path: e.target.value })}
                  placeholder="/users/{id}"
                  disabled={!canEdit}
                  className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground font-mono focus:outline-none focus:border-accent disabled:opacity-50"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-foreground-secondary mb-1">Operation ID</label>
              <input
                type="text"
                value={endpoint.operationId || ''}
                onChange={(e) => updateEndpoint({ operationId: e.target.value || undefined })}
                placeholder="getUserById"
                disabled={!canEdit}
                className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent disabled:opacity-50"
              />
              <p className="text-xs text-foreground-tertiary mt-1">Unique identifier for the operation</p>
            </div>

            <div>
              <label className="block text-sm text-foreground-secondary mb-1">Summary</label>
              <input
                type="text"
                value={endpoint.summary || ''}
                onChange={(e) => updateEndpoint({ summary: e.target.value || undefined })}
                placeholder="Get a user by ID"
                disabled={!canEdit}
                className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent disabled:opacity-50"
              />
            </div>

            <div>
              <label className="block text-sm text-foreground-secondary mb-1">Description</label>
              <textarea
                value={endpoint.description || ''}
                onChange={(e) => updateEndpoint({ description: e.target.value || undefined })}
                placeholder="Detailed description of the endpoint..."
                disabled={!canEdit}
                rows={3}
                className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent disabled:opacity-50 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm text-foreground-secondary mb-1">Tags</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {endpoint.tags?.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-cream/10 text-foreground text-sm rounded"
                  >
                    {tag}
                    {canEdit && (
                      <button
                        onClick={() =>
                          updateEndpoint({
                            tags: endpoint.tags?.filter((t) => t !== tag),
                          })
                        }
                        className="text-foreground-tertiary hover:text-red-400"
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {canEdit && tags.length > 0 && (
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value && !endpoint.tags?.includes(e.target.value)) {
                      updateEndpoint({
                        tags: [...(endpoint.tags || []), e.target.value],
                      });
                    }
                  }}
                  className="px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent"
                >
                  <option value="">Add tag...</option>
                  {tags
                    .filter((t) => !endpoint.tags?.includes(t))
                    .map((tag) => (
                      <option key={tag} value={tag}>
                        {tag}
                      </option>
                    ))}
                </select>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-foreground-secondary">
                <input
                  type="checkbox"
                  checked={endpoint.deprecated || false}
                  onChange={(e) => updateEndpoint({ deprecated: e.target.checked })}
                  disabled={!canEdit}
                  className="rounded"
                />
                Mark as deprecated
              </label>
            </div>
          </div>
        )}

        {/* Parameters Tab (excludes headers) */}
        {activeTab === 'parameters' && (
          <div className="space-y-4">
            {canEdit && (
              <button
                onClick={addParameter}
                className="px-3 py-2 bg-accent-muted text-accent rounded text-sm hover:bg-accent/30"
              >
                + Add Parameter
              </button>
            )}

            {paramCount === 0 ? (
              <p className="text-foreground-tertiary text-center py-8">No parameters defined</p>
            ) : (
              <div className="space-y-4">
                {endpoint.parameters.filter(p => p.in !== 'header').map((param) => (
                  <div
                    key={param.id}
                    className="border border-border rounded-lg p-4"
                  >
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-foreground-tertiary mb-1">Name</label>
                        <input
                          type="text"
                          value={param.name}
                          onChange={(e) =>
                            updateParameter(param.id, { name: e.target.value })
                          }
                          placeholder="paramName"
                          disabled={!canEdit}
                          className="w-full px-2 py-1.5 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-foreground-tertiary mb-1">Location</label>
                        <select
                          value={param.in}
                          onChange={(e) =>
                            updateParameter(param.id, {
                              in: e.target.value as ParameterLocation,
                            })
                          }
                          disabled={!canEdit}
                          className="w-full px-2 py-1.5 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
                        >
                          <option value="query">Query</option>
                          <option value="path">Path</option>
                          <option value="header">Header</option>
                          <option value="cookie">Cookie</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-foreground-tertiary mb-1">Type</label>
                        <select
                          value={param.schema.type}
                          onChange={(e) =>
                            updateParameter(param.id, {
                              schema: { ...param.schema, type: e.target.value as SchemaType },
                            })
                          }
                          disabled={!canEdit}
                          className="w-full px-2 py-1.5 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
                        >
                          <option value="string">String</option>
                          <option value="number">Number</option>
                          <option value="integer">Integer</option>
                          <option value="boolean">Boolean</option>
                          <option value="array">Array</option>
                        </select>
                      </div>
                      <div className="flex items-end gap-4">
                        <label className="flex items-center gap-2 text-sm text-foreground-secondary">
                          <input
                            type="checkbox"
                            checked={param.required}
                            onChange={(e) =>
                              updateParameter(param.id, { required: e.target.checked })
                            }
                            disabled={!canEdit}
                            className="rounded"
                          />
                          Required
                        </label>
                        {canEdit && (
                          <button
                            onClick={() => deleteParameter(param.id)}
                            className="p-1 text-foreground-muted hover:text-red-400"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs text-foreground-tertiary mb-1">Description</label>
                      <input
                        type="text"
                        value={param.description || ''}
                        onChange={(e) =>
                          updateParameter(param.id, { description: e.target.value || undefined })
                        }
                        placeholder="Parameter description"
                        disabled={!canEdit}
                        className="w-full px-2 py-1.5 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Headers Tab */}
        {activeTab === 'headers' && (
          <div className="space-y-4">
            {canEdit && (
              <button
                onClick={() => {
                  const newParam: Parameter = {
                    id: generateId(),
                    name: '',
                    in: 'header',
                    required: false,
                    schema: { type: 'string' },
                  };
                  updateEndpoint({ parameters: [...endpoint.parameters, newParam] });
                }}
                className="px-3 py-2 bg-accent-muted text-accent rounded text-sm hover:bg-accent/30"
              >
                + Add Header
              </button>
            )}

            {/* Common headers helper */}
            {canEdit && (
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-foreground-tertiary">Quick add:</span>
                {['Authorization', 'Content-Type', 'Accept', 'X-Request-ID', 'X-API-Key'].map((header) => {
                  const exists = endpoint.parameters.some(p => p.in === 'header' && p.name === header);
                  if (exists) return null;
                  return (
                    <button
                      key={header}
                      onClick={() => {
                        const newParam: Parameter = {
                          id: generateId(),
                          name: header,
                          in: 'header',
                          required: header === 'Authorization',
                          schema: { type: 'string' },
                          description: header === 'Authorization' ? 'Bearer token for authentication' :
                                       header === 'Content-Type' ? 'Content type of the request body' :
                                       header === 'Accept' ? 'Expected response content type' :
                                       header === 'X-Request-ID' ? 'Unique request identifier for tracing' :
                                       header === 'X-API-Key' ? 'API key for authentication' : undefined,
                        };
                        updateEndpoint({ parameters: [...endpoint.parameters, newParam] });
                      }}
                      className="px-2 py-1 text-xs bg-cream/10 text-foreground-secondary rounded hover:bg-cream/20"
                    >
                      {header}
                    </button>
                  );
                })}
              </div>
            )}

            {headerCount === 0 ? (
              <p className="text-foreground-tertiary text-center py-8">No headers defined</p>
            ) : (
              <div className="space-y-4">
                {endpoint.parameters.filter(p => p.in === 'header').map((param) => (
                  <div
                    key={param.id}
                    className="border border-border rounded-lg p-4"
                  >
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-foreground-tertiary mb-1">Header Name</label>
                        <input
                          type="text"
                          value={param.name}
                          onChange={(e) =>
                            updateParameter(param.id, { name: e.target.value })
                          }
                          placeholder="X-Custom-Header"
                          disabled={!canEdit}
                          className="w-full px-2 py-1.5 bg-cream/10 border border-border rounded text-foreground text-sm font-mono focus:outline-none focus:border-accent disabled:opacity-50"
                        />
                      </div>
                      <div className="flex items-end gap-4">
                        <label className="flex items-center gap-2 text-sm text-foreground-secondary">
                          <input
                            type="checkbox"
                            checked={param.required}
                            onChange={(e) =>
                              updateParameter(param.id, { required: e.target.checked })
                            }
                            disabled={!canEdit}
                            className="rounded"
                          />
                          Required
                        </label>
                        {canEdit && (
                          <button
                            onClick={() => deleteParameter(param.id)}
                            className="p-1 text-foreground-muted hover:text-red-400"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-foreground-tertiary mb-1">Type</label>
                        <select
                          value={param.schema.type}
                          onChange={(e) =>
                            updateParameter(param.id, {
                              schema: { ...param.schema, type: e.target.value as SchemaType },
                            })
                          }
                          disabled={!canEdit}
                          className="w-full px-2 py-1.5 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
                        >
                          <option value="string">String</option>
                          <option value="number">Number</option>
                          <option value="integer">Integer</option>
                          <option value="boolean">Boolean</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-foreground-tertiary mb-1">Example Value</label>
                        <input
                          type="text"
                          value={param.example !== undefined ? String(param.example) : ''}
                          onChange={(e) =>
                            updateParameter(param.id, { example: e.target.value || undefined })
                          }
                          placeholder="Bearer eyJhbGc..."
                          disabled={!canEdit}
                          className="w-full px-2 py-1.5 bg-cream/10 border border-border rounded text-foreground text-sm font-mono focus:outline-none focus:border-accent disabled:opacity-50"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs text-foreground-tertiary mb-1">Description</label>
                      <input
                        type="text"
                        value={param.description || ''}
                        onChange={(e) =>
                          updateParameter(param.id, { description: e.target.value || undefined })
                        }
                        placeholder="Header description"
                        disabled={!canEdit}
                        className="w-full px-2 py-1.5 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Request Body Tab */}
        {activeTab === 'requestBody' && (
          <div className="space-y-4">
            {!endpoint.requestBody ? (
              <div className="text-center py-8">
                <p className="text-foreground-tertiary mb-4">No request body defined</p>
                {canEdit && (
                  <button
                    onClick={() =>
                      updateEndpoint({
                        requestBody: {
                          required: false,
                          content: {
                            'application/json': {
                              schema: { type: 'object', properties: {} },
                            },
                          },
                        },
                      })
                    }
                    className="px-4 py-2 bg-accent-muted text-accent rounded hover:bg-accent/30"
                  >
                    Add Request Body
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-foreground-secondary">
                    <input
                      type="checkbox"
                      checked={endpoint.requestBody.required}
                      onChange={(e) =>
                        updateEndpoint({
                          requestBody: {
                            ...endpoint.requestBody!,
                            required: e.target.checked,
                          },
                        })
                      }
                      disabled={!canEdit}
                      className="rounded"
                    />
                    Required
                  </label>
                  {canEdit && (
                    <button
                      onClick={() => updateEndpoint({ requestBody: undefined })}
                      className="text-sm text-red-400 hover:underline"
                    >
                      Remove Body
                    </button>
                  )}
                </div>

                <div>
                  <label className="block text-sm text-foreground-secondary mb-1">Description</label>
                  <input
                    type="text"
                    value={endpoint.requestBody.description || ''}
                    onChange={(e) =>
                      updateEndpoint({
                        requestBody: {
                          ...endpoint.requestBody!,
                          description: e.target.value || undefined,
                        },
                      })
                    }
                    placeholder="Request body description"
                    disabled={!canEdit}
                    className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent disabled:opacity-50"
                  />
                </div>

                <div>
                  <label className="block text-sm text-foreground-secondary mb-2">Content Type</label>
                  <select
                    value={Object.keys(endpoint.requestBody.content)[0] || 'application/json'}
                    disabled={!canEdit}
                    className="px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
                  >
                    <option value="application/json">application/json</option>
                    <option value="application/x-www-form-urlencoded">application/x-www-form-urlencoded</option>
                    <option value="multipart/form-data">multipart/form-data</option>
                  </select>
                </div>

                {/* Schema Builder */}
                <div className="border border-border rounded-lg p-4">
                  <JsonSchemaBuilder
                    schema={endpoint.requestBody.content['application/json']?.schema || { type: 'object', properties: {} }}
                    onChange={(schema) => {
                      updateEndpoint({
                        requestBody: {
                          ...endpoint.requestBody!,
                          content: {
                            ...endpoint.requestBody!.content,
                            'application/json': {
                              ...endpoint.requestBody!.content['application/json'],
                              schema,
                            },
                          },
                        },
                      });
                    }}
                    example={endpoint.requestBody.content['application/json']?.example}
                    onExampleChange={(example) => {
                      updateEndpoint({
                        requestBody: {
                          ...endpoint.requestBody!,
                          content: {
                            ...endpoint.requestBody!.content,
                            'application/json': {
                              ...endpoint.requestBody!.content['application/json'],
                              example,
                            },
                          },
                        },
                      });
                    }}
                    canEdit={canEdit}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* Responses Tab */}
        {activeTab === 'responses' && (
          <div className="space-y-4">
            {canEdit && (
              <div className="flex gap-2">
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      addResponse(e.target.value);
                    }
                  }}
                  className="px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent"
                >
                  <option value="">Add Response...</option>
                  {Object.entries(COMMON_STATUS_CODES)
                    .filter(
                      ([code]) =>
                        !endpoint.responses.some((r) => r.statusCode === code)
                    )
                    .map(([code, description]) => (
                      <option key={code} value={code}>
                        {code} - {description}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {endpoint.responses.length === 0 ? (
              <p className="text-foreground-tertiary text-center py-8">No responses defined</p>
            ) : (
              <div className="space-y-4">
                {endpoint.responses
                  .sort((a, b) => a.statusCode.localeCompare(b.statusCode))
                  .map((response) => {
                    const isSuccess = response.statusCode.startsWith('2');
                    const isError = response.statusCode.startsWith('4') || response.statusCode.startsWith('5');

                    return (
                      <div
                        key={response.id}
                        className={`border rounded-lg p-4 ${
                          isSuccess
                            ? 'border-green-500/30'
                            : isError
                              ? 'border-red-500/30'
                              : 'border-border'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span
                            className={`text-lg font-bold ${
                              isSuccess
                                ? 'text-green-400'
                                : isError
                                  ? 'text-red-400'
                                  : 'text-foreground'
                            }`}
                          >
                            {response.statusCode}
                          </span>
                          <div className="flex items-center gap-2">
                            {/* Toggle JSON body */}
                            {canEdit && !response.content && (
                              <button
                                onClick={() => {
                                  updateResponse(response.id, {
                                    content: {
                                      'application/json': {
                                        schema: { type: 'object', properties: {} },
                                      },
                                    },
                                  });
                                }}
                                className="px-2 py-1 text-xs bg-accent-muted text-accent rounded hover:bg-accent/30"
                              >
                                + Add JSON Body
                              </button>
                            )}
                            {canEdit && (
                              <button
                                onClick={() => deleteResponse(response.id)}
                                className="p-1 text-foreground-muted hover:text-red-400"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="mb-3">
                          <label className="block text-xs text-foreground-tertiary mb-1">Description</label>
                          <input
                            type="text"
                            value={response.description}
                            onChange={(e) =>
                              updateResponse(response.id, { description: e.target.value })
                            }
                            disabled={!canEdit}
                            className="w-full px-2 py-1.5 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent disabled:opacity-50"
                          />
                        </div>

                        {/* Response JSON Schema */}
                        {response.content && response.content['application/json'] && (
                          <div className="mt-4 border-t border-border-muted pt-4">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm text-foreground-secondary">Response Body (JSON)</span>
                              {canEdit && (
                                <button
                                  onClick={() => {
                                    updateResponse(response.id, { content: undefined });
                                  }}
                                  className="text-xs text-red-400 hover:underline"
                                >
                                  Remove Body
                                </button>
                              )}
                            </div>
                            <JsonSchemaBuilder
                              schema={response.content['application/json'].schema || { type: 'object', properties: {} }}
                              onChange={(schema) => {
                                updateResponse(response.id, {
                                  content: {
                                    ...response.content,
                                    'application/json': {
                                      ...response.content!['application/json'],
                                      schema,
                                    },
                                  },
                                });
                              }}
                              example={response.content['application/json'].example}
                              onExampleChange={(example) => {
                                updateResponse(response.id, {
                                  content: {
                                    ...response.content,
                                    'application/json': {
                                      ...response.content!['application/json'],
                                      example,
                                    },
                                  },
                                });
                              }}
                              canEdit={canEdit}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default EndpointEditor;
